import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useCookies } from "react-cookie";
import { useQuery } from "@tanstack/react-query";
import { fetchPalleteLocations } from "../../../api/warehouse.service";
import { allWarehouseLocations } from "../../../constants";
import { LocationData } from "../../../types/data";
import { validateEan } from "../../../utils";

export enum Warehouse {
  NONE,
  ASMAT,
  JAMS
}

const MINIMUM_BLUR_TIME = 1000;

export const useFindLogic = () => {
  const [cookie, setCookie, removeCookie] = useCookies(["access_key", "eans"]);
  const getInitialEans = (cookieEans: any) => {
    if (cookieEans) return String(cookieEans).split(",");
    return [];
  };

  const [eanField, setEanField] = useState("");
  const [selectedEAN, setSelectedEAN] = useState("");
  const [newData, setNewData] = useState<string[]>(() => getInitialEans(cookie.eans));
  const [lastSearched, setLastSearched] = useState<string[]>([]);
  const [filterShelf, setFilterShelf] = useState("");
  const [warehouse, setWarehouse] = useState<Warehouse>(Warehouse.NONE);
  const [confirmClear, setConfirmClear] = useState(false);
  const [formError, setFormError] = useState("");

  const [bufferedData, setBufferedData] = useState<LocationData[] | undefined>(undefined);
  const [isMinimumBlurActive, setIsMinimumBlurActive] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const findQuery = useQuery<LocationData[]>({
    queryKey: ["findPalettes", lastSearched],
    queryFn: () => fetchPalleteLocations(lastSearched, cookie.access_key),
    enabled: lastSearched.length > 0,
    retry: false,
    staleTime: 0,
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (!findQuery.isFetching && isMinimumBlurActive) {
      timer = setTimeout(() => {
        setBufferedData(findQuery.data);
        setIsMinimumBlurActive(false);
      }, MINIMUM_BLUR_TIME);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [findQuery.isFetching, findQuery.data, isMinimumBlurActive]);

  useEffect(() => {
    if (findQuery.isError) {
      setIsMinimumBlurActive(false);
      setBufferedData(undefined);
    }
  }, [findQuery.isError]);

  const [isErrorExiting, setIsErrorExiting] = useState(false);

  const isSyncing = findQuery.isFetching || isMinimumBlurActive;

  const filteredData = useMemo(() => {
    if (!bufferedData) return undefined;

    return bufferedData
      .map((location) => ({
        ...location,

        // COFNAC GDY BEDA EANY STALEJ DLUGOSCI
        // results: location.results.filter((r) => newData.includes(r.ean))

        results: location.results.filter((r) => newData.some((searchPhrase) => r.ean.endsWith(searchPhrase)))
      }))
      .filter((location) => location.results.length > 0)
      .sort((a, b) => {
        if (b.results.length !== a.results.length) {
          return b.results.length - a.results.length;
        }
        return a.location.localeCompare(b.location);
      });
  }, [bufferedData, newData]);

  const eanStatusMap = useMemo(() => {
    const map = new Map();

    // 1. Zaznaczony (priorytet)
    if (selectedEAN) {
      map.set(selectedEAN, "selected");
    }

    // 2. Jeśli trwa synchronicacja lub błąd, reszta jest pusta
    if (isSyncing || findQuery.isError || !bufferedData) return map;

    // 3. Znalezione
    // Oryginalna logika: filteredData?.some((loc) => loc.results.some((r) => r.ean.endsWith(ean)))
    if (filteredData) {
      filteredData.forEach((loc) => {
        loc.results.forEach((r) => {
          // Musimy znaleźć, który z naszych EAN-ów pasuje do tego wyniku
          newData.forEach((ean) => {
            if (r.ean.endsWith(ean)) {
              const current = map.get(ean) || "";
              // Jeśli nie jest selected, ustawiamy founded
              if (current !== "selected") {
                map.set(ean, "founded");
              }
            }
          });
        });
      });
    }

    // 4. Nieznalezione
    // Oryginalna logika: lastSearched.includes(ean)
    lastSearched.forEach((ean) => {
      if (!map.has(ean)) {
        map.set(ean, "notFounded");
      }
    });

    return map;
  }, [selectedEAN, isSyncing, findQuery.isError, bufferedData, filteredData, lastSearched, newData]);

  // Nowa, zoptymalizowana funkcja
  const getEANType = useCallback((ean: string) => eanStatusMap.get(ean) || "", [eanStatusMap]);

  // const getEANType = useCallback(
  //   (ean: string) => {
  //     if (selectedEAN === ean) return "selected";
  //     if (isSyncing) return "";
  //     if (findQuery.isError || !bufferedData) return "";

  //     // COFNAC GDY BEDA EANY STALEJ DLUGOSCI
  //     // const isFounded = filteredData?.some((loc) => loc.results.some((r) => r.ean === ean));
  //     const isFounded = filteredData?.some((loc) => loc.results.some((r) => r.ean.endsWith(ean)));

  //     if (isFounded) return "founded";
  //     if (lastSearched.includes(ean)) return "notFounded";

  //     return "";
  //   },
  //   [selectedEAN, isSyncing, findQuery.isError, bufferedData, filteredData, lastSearched]
  // );

  const errorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const actions = useMemo(
    () => ({
      setEanField: (val: string) => {
        setEanField(val);
        if (formError && !isErrorExiting) {
          if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
          setIsErrorExiting(true);
          errorTimeoutRef.current = setTimeout(() => {
            setFormError("");
            setIsErrorExiting(false);
          }, 200);
        }
      },
      setFilterShelf,

      addEan: (input: string) => {
        if (!input) return;
        const rawArray = input.split(",").map((e) => e.trim());
        const invalidEans = rawArray.filter((e) => !validateEan(e));

        if (invalidEans.length > 0) {
          setFormError(`${rawArray.length > 1 ? "Każdy EAN" : "EAN"} musi składać się z czterech cyfr.`);
          return;
        }

        setNewData((prev) => Array.from(new Set([...prev, ...rawArray])));
        setFormError("");
        setIsErrorExiting(false);
        setEanField("");
      },

      removeEan: (ean: string) => {
        if (selectedEAN !== ean) {
          setSelectedEAN(ean);
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          timeoutRef.current = setTimeout(() => setSelectedEAN(""), 1000);
          return;
        }
        setNewData((prev) => prev.filter((e) => e !== ean));
        setSelectedEAN("");
      },

      find: () => {
        if (newData.length === 0) return;
        setIsMinimumBlurActive(true);
        setCookie("eans", newData.join(","), { maxAge: 60 * 60 * 24 * 14 });
        setFilterShelf("");
        setLastSearched([...newData]);
        // setTimeout(() => findQuery.refetch(), 0);
      },

      handleClearClick: () => {
        if (!confirmClear) {
          setConfirmClear(true);
          setTimeout(() => setConfirmClear(false), 3000);
          return;
        }
        setNewData([]);
        setLastSearched([]);
        setBufferedData(undefined);
        removeCookie("eans");
        setConfirmClear(false);
      },

      changeWarehouse: () => {
        setWarehouse((prev) => {
          const sequence = {
            [Warehouse.NONE]: Warehouse.ASMAT,
            [Warehouse.ASMAT]: Warehouse.JAMS,
            [Warehouse.JAMS]: Warehouse.NONE
          };
          return sequence[prev];
        });
      },
      removeNotFound: () => {
        setNewData((prev) =>
          prev.filter((ean) => {
            const type = getEANType(ean);
            return type === "founded" || type === "selected";
          })
        );
      }
    }),
    [newData, confirmClear, formError, isErrorExiting, selectedEAN, getEANType, setCookie, removeCookie]
  );

  const hasNotFound = useMemo(() => newData.some((ean) => getEANType(ean) === "notFounded"), [newData, getEANType]);

  return {
    state: {
      data: filteredData,
      newData,
      eanField,
      confirmClear,
      filterShelf,
      warehouse,
      getEANType,
      formError,
      hasNotFound,
      isErrorExiting,
      isSyncing: findQuery.isFetching || isMinimumBlurActive,
      showSkeleton: lastSearched.length > 0 && !bufferedData && (findQuery.isFetching || isMinimumBlurActive),
      errorMessage: findQuery.isError
        ? (findQuery.error as any)?.response?.data?.message || "Błąd połączenia z serwerem."
        : "",
      warehouseLocations:
        warehouse === Warehouse.ASMAT
          ? allWarehouseLocations.ASMAT
          : warehouse === Warehouse.JAMS
            ? allWarehouseLocations.JAMS
            : []
    },
    actions
  };
};
